import { computed, inject, Injectable, signal } from '@angular/core';
import { LanguageService } from '../i18n/language.service';

const SHORT_MONTHS: Record<string, string[]> = {
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  pt: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
};

function fmtISO(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Shared search state (destination, dates, guests) across the marketplace flow. */
@Injectable({ providedIn: 'root' })
export class SearchStateService {
  private readonly language = inject(LanguageService);

  /** Empty means "all destinations" (no city filter). */
  readonly dest = signal<string>('');

  /** Human label for the destination field/title. */
  readonly destLabel = computed(() => this.dest() || this.language.translate('search.allDestinations'));

  /** check-in / check-out as YYYY-MM-DD (null until chosen). */
  readonly from = signal<string | null>(this.defaultFrom());
  readonly to = signal<string | null>(this.defaultTo());

  readonly guests = signal<number>(2);
  readonly pets = signal<number>(0);

  readonly nights = computed(() => {
    const f = this.from();
    const t = this.to();
    if (!f || !t) return 0;
    const ms = new Date(t).getTime() - new Date(f).getTime();
    return Math.max(0, Math.round(ms / 86_400_000));
  });

  readonly datesLabel = computed(() => {
    const f = this.from();
    const t = this.to();
    if (!f || !t) return this.language.translate('search.addDates');
    return `${this.short(f)} – ${this.short(t)}`;
  });

  readonly checkInLabel = computed(() => (this.from() ? this.short(this.from()!) : this.language.translate('search.add')));
  readonly checkOutLabel = computed(() => (this.to() ? this.short(this.to()!) : this.language.translate('search.add')));

  readonly guestsLabel = computed(() => {
    const g = this.guests();
    return this.language.translate(g === 1 ? 'search.guestCountOne' : 'search.guestCountOther', { count: g });
  });

  setDest(dest: string): void {
    this.dest.set(dest);
  }

  setRange(from: string, to: string): void {
    this.from.set(from);
    this.to.set(to);
  }

  incGuest(): void {
    this.guests.update((g) => Math.min(16, g + 1));
  }

  decGuest(): void {
    this.guests.update((g) => Math.max(1, g - 1));
  }

  private short(iso: string): string {
    const d = new Date(iso + 'T00:00:00');
    const lang = this.language.activeLang();
    return `${d.getDate()} ${SHORT_MONTHS[lang]?.[d.getMonth()] ?? SHORT_MONTHS['es'][d.getMonth()]}`;
  }

  private defaultFrom(): string {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return fmtISO(d);
  }

  private defaultTo(): string {
    const d = new Date();
    d.setDate(d.getDate() + 18);
    return fmtISO(d);
  }
}
